import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, LayoutGrid, Grid2x2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { ProductCard } from "../components/ProductCard";
import { ConnectState } from "../components/ConnectState";
import { Reveal } from "../components/Reveal";
import { useProducts } from "../lib/api";
import { PreviewBanner } from "../components/PreviewBanner";

const SORTS = [
  { value: "newest", label: "Newest" },
  { value: "best-selling", label: "Best Selling" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "title", label: "A–Z" },
];

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const type = searchParams.get("type") || "";
  const sort = searchParams.get("sort") || "newest";
  const [input, setInput] = useState(q);
  const [cols, setCols] = useState(4);

  const state = useProducts({
    first: 48,
    q: q || undefined,
    product_type: type || undefined,
    sort,
  });
  const all = useProducts({ first: 100 });

  const types = useMemo(
    () => [...new Set(all.products.map((p) => p.productType).filter(Boolean))],
    [all.products]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      if (input !== q) {
        const next = Object.fromEntries(searchParams);
        if (input) next.q = input;
        else delete next.q;
        setSearchParams(next, { replace: true });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const setParam = (key, value) => {
    const next = Object.fromEntries(searchParams);
    if (value) next[key] = value;
    else delete next[key];
    setSearchParams(next);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Shop — Full catalog"
        title="All products"
        description="Every piece, live from the TRAILNONE store. Filter, search and find your fit."
      />

      <div className="sticky top-16 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 sm:px-10 lg:px-16">
          <div className="flex min-w-52 flex-1 items-center gap-2 border-b border-border focus-within:border-signal">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="SEARCH THE CATALOG"
              data-testid="search-input"
              className="w-full bg-transparent py-2.5 font-mono text-xs uppercase tracking-[0.2em] placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            data-testid="sort-select"
            className="border border-border bg-background px-3 py-2.5 font-mono text-xs uppercase tracking-[0.15em] focus:border-signal focus:outline-none"
          >
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <div className="hidden gap-1 lg:flex">
            <button
              onClick={() => setCols(2)}
              data-testid="grid-2-toggle"
              aria-label="Two column grid"
              className={`p-2 transition-colors ${cols === 2 ? "text-signal" : "text-muted-foreground hover:text-foreground"}`}
            >
              <Grid2x2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCols(4)}
              data-testid="grid-4-toggle"
              aria-label="Four column grid"
              className={`p-2 transition-colors ${cols === 4 ? "text-signal" : "text-muted-foreground hover:text-foreground"}`}
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
          </div>
        </div>
        {types.length > 0 && (
          <div className="flex gap-2 overflow-x-auto px-5 pb-4 sm:px-10 lg:px-16" data-testid="category-filters">
            <button
              onClick={() => setParam("type", "")}
              data-testid="filter-all"
              className={`shrink-0 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                !type
                  ? "border-signal bg-signal text-white"
                  : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
              }`}
            >
              All
            </button>
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setParam("type", t === type ? "" : t)}
                data-testid={`filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`shrink-0 border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                  type === t
                    ? "border-signal bg-signal text-white"
                    : "border-border text-muted-foreground hover:border-foreground hover:text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="px-5 py-12 sm:px-10 lg:px-16">
        {state.loading ? (
          <div className={`grid grid-cols-2 gap-3 ${cols === 2 ? "lg:grid-cols-2" : "lg:grid-cols-4"}`}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-secondary" />
            ))}
          </div>
        ) : !state.connected && state.products.length === 0 ? (
          <Reveal>
            <ConnectState />
          </Reveal>
        ) : state.products.length === 0 ? (
          <p
            data-testid="no-products-message"
            className="border border-border p-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground"
          >
            Nothing found{q ? ` for "${q}"` : ""} — try another search
          </p>
        ) : (
          <>
            {!state.connected && <PreviewBanner />}
            <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="results-count">
              {state.products.length} product{state.products.length !== 1 ? "s" : ""}
              {q && ` — "${q}"`}
              {type && ` — ${type}`}
            </p>
            <div className={`grid grid-cols-2 gap-3 ${cols === 2 ? "lg:grid-cols-2" : "lg:grid-cols-4"}`}>
              {state.products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i % 8} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
